/**
 * light.ts - FMwvTool Default Light Mode Skin
 * 
 * Complete light mode theme with all 122 core variables.
 * Based on consolidated variable mapping system.
 * Designed for optimal readability in bright environments.
 */

import { sharedLayout } from './shared-layout.js';

/**
 * fmwvtoolDefaultLight constant
 */
export const fmwvtoolDefaultLight = {
  id: 'fmwvtool-default-light',
  name: 'FMwvTool Default (Light)',
  description: 'Professional light theme with complete variable coverage',
  mode: 'light' as const,
  
  variables: {
    // ========================================================================
    // TIER 1: UNIVERSAL VARIABLES (17)
    // ========================================================================
    
    // Interactive States (5)
    '--interactive-hover': 'rgba(0, 0, 0, 0.05)',
    '--interactive-active': 'rgba(0, 0, 0, 0.1)',
    '--interactive-selected': '#e3f2fd',
    '--interactive-disabled': '#f3f4f6',
    '--interactive-focus': '#3b82f6',
    
    // Surface Hierarchy (4)
    '--surface-base': '#ffffff',
    '--surface-raised': '#f8f9fa',
    '--surface-overlay': '#ffffff',
    '--surface-popover': '#1f2937',
    
    // Text Hierarchy (5)
    '--text-primary': '#212529',
    '--text-secondary': '#6c757d',
    '--text-tertiary': '#9ca3af',
    '--text-disabled': '#d1d5db',
    '--text-inverse': '#ffffff', // Also used in main.css
    
    // Borders (3)
    '--border-default': '#e0e0e0',
    '--border-subtle': '#f0f0f0',
    '--border-strong': '#3b82f6',
    
    // ========================================================================
    // TIER 2: COMPONENT PATTERN VARIABLES (54)
    // ========================================================================
    
    // Dropdown Pattern (8)
    '--dropdown-bg': '#ffffff',
    '--dropdown-border': '#e0e0e0',
    '--dropdown-shadow': '0 4px 12px rgba(0, 0, 0, 0.15)',
    '--dropdown-item-bg': 'transparent',
    '--dropdown-item-hover': 'rgba(0, 0, 0, 0.05)',
    '--dropdown-item-active': 'rgba(0, 0, 0, 0.1)',
    '--dropdown-item-selected': '#e3f2fd',
    '--dropdown-item-disabled': '#f3f4f6',
    
    // Table Pattern (10)
    '--table-bg': '#ffffff',
    '--table-border': '#e0e0e0',
    '--table-header-bg': '#f8f9fa',
    '--table-header-text': '#212529',
    '--table-header-border': '#e0e0e0',
    '--table-row-bg': '#ffffff',
    '--table-row-alt': '#f8f9fa',
    '--table-row-hover': 'rgba(0, 0, 0, 0.03)',
    '--table-row-selected': '#e3f2fd',
    '--table-cell-border': '#e0e0e0',
    
    // Card Pattern (6)
    '--card-bg': '#ffffff',
    '--card-border': '#e0e0e0',
    '--card-shadow': '0 2px 8px rgba(0, 0, 0, 0.1)',
    '--card-hover': 'rgba(0, 0, 0, 0.02)',
    '--card-header-bg': '#f8f9fa',
    '--card-header-text': '#212529',
    
    // Input Pattern (8)
    '--input-bg': '#ffffff',
    '--input-bg-focus': '#ffffff',
    '--input-bg-disabled': '#f3f4f6',
    '--input-text': '#212529',
    '--input-placeholder': '#9ca3af',
    '--input-border': '#d1d5db',
    '--input-border-focus': '#3b82f6',
    '--input-border-error': '#ef4444',
    
    // Modal Pattern (6)
    '--modal-bg': '#ffffff',
    '--modal-overlay': 'rgba(0, 0, 0, 0.5)',
    '--modal-border': '#e0e0e0',
    '--modal-shadow': '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    '--modal-header-bg': '#f8f9fa',
    '--modal-footer-bg': '#f8f9fa',
    
    // Button Pattern (8)
    '--button-bg': '#3b82f6',
    '--button-bg-hover': '#2563eb',
    '--button-bg-active': '#1d4ed8',
    '--button-text': '#ffffff',
    '--button-text-hover': '#ffffff',
    '--button-border': '#3b82f6',
    '--button-disabled-bg': '#e5e7eb',
    '--button-disabled-text': '#9ca3af',
    
    // Navigation Pattern (8)
    '--nav-bg': '#ffffff',
    '--nav-border': '#e0e0e0',
    '--nav-text': '#212529',
    '--nav-text-hover': '#3b82f6',
    '--nav-item-bg': 'transparent',
    '--nav-item-hover': 'rgba(0, 0, 0, 0.05)',
    '--nav-item-active': '#e3f2fd',
    '--nav-item-selected': '#e3f2fd',
    
    // ========================================================================
    // TIER 3: SEMANTIC COLOR VARIABLES (15)
    // ========================================================================
    
    // Primary (3)
    '--color-primary': '#3b82f6',
    '--color-primary-hover': '#2563eb',
    '--color-primary-light': '#dbeafe',
    
    // Success (3)
    '--color-success': '#10b981',
    '--color-success-hover': '#059669',
    '--color-success-light': '#d1fae5',
    
    // Warning (3)
    '--color-warning': '#f59e0b',
    '--color-warning-hover': '#d97706',
    '--color-warning-light': '#fef3c7',
    
    // Error (3)
    '--color-error': '#ef4444',
    '--color-error-hover': '#dc2626',
    '--color-error-light': '#fee2e2',
    
    // Info (3)
    '--color-info': '#3b82f6',
    '--color-info-hover': '#2563eb',
    '--color-info-light': '#dbeafe',
    
    // ========================================================================
    // TIER 4: UTILITY VARIABLES (imported from shared-layout.ts)
    // ========================================================================
    ...sharedLayout,
    
    // Shadows (theme-specific - light mode)
    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.05)',
    '--shadow-md': '0 4px 6px rgba(0, 0, 0, 0.1)',
    '--shadow-lg': '0 10px 15px rgba(0, 0, 0, 0.1)',
    '--shadow-xl': '0 20px 25px rgba(0, 0, 0, 0.15)',
    '--shadow-inner': 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
  },
  
  // Plugin-specific configurations (from original skin)
  plugins: {
    outliner: {
      appearance: {
        nodeIndent: 20,
        nodeHeight: 28,
        fontSize: 14,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        showLines: true,
        compactMode: false,
      }
    },
    pdfViewer: {
      appearance: {
        backgroundColor: '#f5f5f5',
        toolbarHeight: 48,
        sidebarWidth: 250,
      }
    },
    picker: {
      appearance: {
        itemHeight: 32,
        fontSize: 14,
        showIcons: true,
      }
    },
    fieldMatcher: {
      appearance: {
        confidenceColors: {
          high: '#10b981',
          medium: '#f59e0b',
          low: '#ef4444',
        }
      }
    },
    tagManager: {
      appearance: {
        tagHeight: 24,
        tagPadding: '4px 8px',
        tagBorderRadius: '12px',
      }
    }
  }
};

export default fmwvtoolDefaultLight;
